import { useEffect, useRef } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import {
  completedOnboardingKeys,
  isGuidedOnboardingSearch,
  nextGuidedStepKey,
  ONBOARDING_GUIDED_QUERY,
  stripGuidedOnboardingParams,
} from '../../../lib/onboarding/onboardingGuided'
import {
  resolveGuidedHydrationAction,
  resolveJourneyOmEntryAction,
} from '../../../lib/onboarding/guidedHydrationPolicy'
import {
  isOnboardingGuideCoreComplete,
} from '../../../lib/onboarding/onboardingGuideConfig'
import { isOnboardingForfeited } from '../../../lib/onboarding/onboardingForfeitCache'
import { isOnboardingJourneyActive } from '../../../lib/onboarding/onboardingSidebarGating'
import { parseOnboardingStepParam } from '../../../lib/onboarding/onboardingOfferManagementGating'
import { useOnboardingGuidedStore } from '../../../store/onboardingGuidedStore'
import { useAuthStore } from '../../../store/authStore'
import { useOnboardingStatus, useStartOnboardingGuide } from './useOnboarding'

/**
 * Sole entry point for restoring / syncing guided session.
 * - URL ?onboarding=guided → start/ensure from params
 * - Mid-journey OM without URL → start next hub step + sync guided params
 * Banner and OM tutorial filter must not call start/ensure themselves.
 */
export function useOnboardingGuidedHydration() {
  const { pathname } = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin')
  const { data: onboardingStatus, isFetched } = useOnboardingStatus()
  const { mutateAsync: markGuideStarted } = useStartOnboardingGuide()
  const forfeited = isOnboardingForfeited(onboardingStatus)
  const markStartedOnceRef = useRef(false)

  useEffect(() => {
    if (isAdmin) return

    const inGuidedUrl = isGuidedOnboardingSearch(searchParams.toString())

    if (forfeited) {
      if (inGuidedUrl || useOnboardingGuidedStore.getState().active) {
        useOnboardingGuidedStore.getState().stop()
        if (inGuidedUrl) {
          setSearchParams((prev) => stripGuidedOnboardingParams(prev), { replace: true })
        }
      }
      return
    }

    if (!isFetched) return

    if (
      !onboardingStatus?.opted_in &&
      !onboardingStatus?.forfeited &&
      !markStartedOnceRef.current &&
      (inGuidedUrl || pathname === '/allegro/offer-management')
    ) {
      markStartedOnceRef.current = true
      void markGuideStarted().catch(() => {
        markStartedOnceRef.current = false
      })
    }

    const store = useOnboardingGuidedStore.getState()

    if (inGuidedUrl) {
      const stepFromUrl = parseOnboardingStepParam(searchParams.toString())
      const action = resolveGuidedHydrationAction({
        storeActive: store.active,
        storeStep: store.currentStep,
        stepFromUrl,
      })

      if (action.type === 'start') {
        store.start(action.stepKey)
      } else if (action.type === 'ensure') {
        store.ensure(action.stepKey)
      }
      return
    }

    const completedKeys = completedOnboardingKeys(onboardingStatus)
    const journeyActive =
      isOnboardingJourneyActive(onboardingStatus, completedKeys) &&
      !isOnboardingGuideCoreComplete(completedKeys)
    const omAction = resolveJourneyOmEntryAction({
      pathname,
      storeActive: store.active,
      journeyActive,
      nextHubStep: nextGuidedStepKey(completedKeys),
    })

    if (omAction.type !== 'start' || !omAction.stepKey) return

    store.start(omAction.stepKey)
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('onboarding', ONBOARDING_GUIDED_QUERY)
        next.set('onboardingStep', omAction.stepKey!)
        return next
      },
      { replace: true },
    )
  }, [
    pathname,
    searchParams,
    isAdmin,
    forfeited,
    isFetched,
    setSearchParams,
    onboardingStatus,
    markGuideStarted,
  ])
}
